/**
 * The media allowance as a church reads it: how much of the plan's storage is
 * used, and on what. The API counts (`GET /media/usage`); this only words it.
 */

export interface MediaUsageInput {
  usedBytes: number
  /** Null: unlimited. */
  limitBytes: number | null
  breakdown: {
    library: number
    planFiles: number
    seriesArt: number
    photos: number
    resources: number
    /** A song's own artwork and recording (purpose `song_media`); absent from older callers. */
    songMedia?: number
  }
}

/** Decimal units, as plans are sold (1 GB = 10⁹ bytes, the way the limit is counted). */
export function formatBytes(bytes: number): string {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(bytes >= 1e11 ? 0 : 1)} GB`
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(bytes >= 1e8 ? 0 : 1)} MB`
  if (bytes >= 1e3) return `${Math.round(bytes / 1e3)} KB`
  return `${bytes} B`
}

const PARTS = [
  { key: 'library', label: 'ForStream library' },
  { key: 'planFiles', label: 'Plan and series files' },
  { key: 'seriesArt', label: 'Series art' },
  { key: 'photos', label: 'Photos' },
  { key: 'resources', label: 'Group files' },
  { key: 'songMedia', label: 'Song artwork and recordings' },
] as const

export function mediaUsageSummary(usage: MediaUsageInput) {
  const percent =
    usage.limitBytes === null
      ? null
      : usage.limitBytes === 0
        ? 100
        : Math.min(100, Math.round((usage.usedBytes / usage.limitBytes) * 100))
  return {
    used: formatBytes(usage.usedBytes),
    limit: usage.limitBytes === null ? null : formatBytes(usage.limitBytes),
    /** 0–100, or null when unlimited. */
    percent,
    tone: percent === null || percent < 80 ? 'ok' : percent < 100 ? 'warning' : 'full',
    /** What takes the space, largest first; nothing listed that takes none. */
    parts: PARTS.map((p) => ({
      key: p.key,
      label: p.label,
      bytes: usage.breakdown[p.key] ?? 0,
      text: formatBytes(usage.breakdown[p.key] ?? 0),
    }))
      .filter((p) => p.bytes > 0)
      .sort((a, b) => b.bytes - a.bytes),
  } as const
}
